import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FlightOffer } from "../types/flight";
import {
    getSearchData,
    saveSearchData,
    clearSearchData,
    StoredSearchData,
} from "../utils/storageUtils";

interface UseFlightSearchReturn {
    searchData: StoredSearchData | null;
    isLoading: boolean;
    hasResults: boolean;
    navigateToResults: (
        params: StoredSearchData["searchParams"],
        results: FlightOffer[],
        dictionaries?: Record<string, any>
    ) => void;
    navigateToBooking: () => void;
    clearResults: () => void;
}

export const useFlightSearch = (): UseFlightSearchReturn => {
    const navigate = useNavigate();
    const [searchData, setSearchData] = useState<StoredSearchData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Load search data from sessionStorage on mount
        const loadSearchData = () => {
            try {
                const data = getSearchData();
                setSearchData(data);
            } catch (error) {
                console.error("Error loading search data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadSearchData();

        // Listen for storage events (if user has multiple tabs open)
        const handleStorageChange = (e: StorageEvent) => {
            if (
                e.key === "philippineAirlines.searchResults" ||
                e.key === "philippineAirlines.searchParams"
            ) {
                loadSearchData();
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    const navigateToResults = (
        params: StoredSearchData["searchParams"],
        results: FlightOffer[],
        dictionaries?: Record<string, any>
    ) => {
        // Save to sessionStorage
        saveSearchData(params, results, dictionaries);

        // Update local state
        setSearchData({
            searchParams: params,
            searchResults: results,
            dictionaries,
            timestamp: Date.now(),
        });

        // Navigate to results page
        navigate("/philippine-airlines/results");
    };

    const navigateToBooking = () => {
        navigate("/philippine-airlines");
    };

    const clearResults = () => {
        clearSearchData();
        setSearchData(null);
        navigateToBooking();
    };

    return {
        searchData,
        isLoading,
        hasResults: Boolean(searchData?.searchResults?.length),
        navigateToResults,
        navigateToBooking,
        clearResults,
    };
};
